import { Box, Divider, Typography } from "@mui/material";
import { throttle } from "lodash";
import { observer } from "mobx-react-lite";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SolidGradientColorPicker from "../../../../../../../components/common/SolidGradientColorPicker/SolidGradientColorPicker";
import { useAppStore } from "../../../../../../../hooks/useStores";
import { CampaignEntityStatus } from "../../../../../../../models/common";
import useDesignEditorUtils, { FeatureType } from "../../../../../../CustomUtils/UseDesignEditor";
import StepperNavigation from "../../../../../../StepperNavigation/StepperNavigation";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader";
import { DesignEditorContext } from "../../../../../../contexts/DesignEditor";
import { useActiveObject } from "../../../../../../hooks/useActiveObject";
import { useEditor } from "../../../../../../hooks/useEditor";
import { BrandDefinition } from "../../../../../../models/brandDefinition";
import { IStaticText, LayerType } from "../../../../../../types";
import styles from "./TextHighlighter.module.scss";

const TextHighlighter = () => {
	const { t: tEditorTabs } = useTranslation("translation", { keyPrefix: "editor.tabs" });
	const { isPopupView } = useContext(DesignEditorContext);
	const editor = useEditor();
	const [brands, setBrands] = useState<BrandDefinition[]>([]);
	const { brandsDefinitionStore, campaignStore } = useAppStore();
	const activeObject = useActiveObject() as Required<IStaticText>;
	const activeObjectColor = activeObject?.backgroundColor;
	const [color, setColor] = useState(activeObjectColor);
	const { EditAllAds } = useDesignEditorUtils();
	useEffect(() => {
		if (brandsDefinitionStore.brands.items.length == 0) {
			loadBrands();
		} else {
			setBrands(brandsDefinitionStore.brands.items.filter((item) => item.status === CampaignEntityStatus.ACTIVE));
		}
	}, []);

	useEffect(() => {
		if (editor) {
			if (activeObject && activeObject.type === "StaticText") {
				setColor(activeObject.textBackgroundColor);
			}
		}
	}, [editor, activeObject]);

	const updateObjectFill = throttle((color: string) => {
		if (activeObject) {
			editor?.objects.update(
				{
					textBackgroundColor: color,
					brandId: undefined,
				},
				undefined,
				!campaignStore.isEditingAllAds,
			);

			EditAllAds(LayerType.STATIC_TEXT, activeObject.textType, FeatureType.TEXTHIGHLIGHTER, color);
		}
	}, 100);

	const loadBrands = () => {
		brandsDefinitionStore.loadBrandsDefinition().then(() => {
			setBrands(brandsDefinitionStore.brands.items.filter((item) => item.status === "Active"));
		});
	};

	return (
		<>
			<Box className={styles.container}>
				<MainTabHeader title={tEditorTabs("highlighterColor")} />
				<Box className={`${styles.tabs} ${styles.fullHeight}`}>
					<Box className={styles.ColorFillContent}>
						<Box className={styles.colorPickerContainer}>
							<SolidGradientColorPicker
								color={color}
								onChange={(color: string) => {
									setColor(color);
									updateObjectFill(color);
								}}
								hideColorTypeBtns={true}
							/>
						</Box>
						<Divider className={styles.mainDivider} />
						<Typography className={styles.mainLabel}>{tEditorTabs("brandColors")}</Typography>
						<Box className={styles.BrandsContainer}>
							{brands.map((brand: BrandDefinition, idx: number) => {
								const colors = brand?.color_pallete?.colors;
								return (
									<>
										<Box className={styles.brandColorsRow}>
											<Typography className={styles.brandNameLabel}>
												{brand.name.length < 10 ? brand.name : brand.name.slice(0, 10) + "..."}
											</Typography>

											{colors?.map((color: string, index: number) => (
												<Box
													onClick={() => {
														updateObjectFill(color);
													}}
													key={index}
												>
													<Box className={styles.itemContent}>
														<Box
															sx={{ background: color }}
															className={styles.itemPreview}
														></Box>
													</Box>
												</Box>
											))}
										</Box>
										{idx != brands.length - 1 && <Divider className={styles.divider} />}
									</>
								);
							})}
						</Box>
					</Box>
				</Box>
				{isPopupView && <StepperNavigation />}
			</Box>
		</>
	);
};

const ObservedComponent = observer(TextHighlighter);
export default ObservedComponent;
